import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useSelector } from "react-redux";
import { getApiBaseUrl } from "../../config/api";
import { WEEK_DAYS } from "../admin/calendarRelated/timetableUtils";

function KpiMini({ title, value, subtitle }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {title}
            </Typography>
            <Typography variant="h6" fontWeight={800} color="primary.main">
                {value}
            </Typography>
            {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            ) : null}
        </Paper>
    );
}

const TeacherTimetablePage = () => {
    const { currentUser } = useSelector((state) => state.user);
    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;

    const now = new Date();
    const baseYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    const academicYearOptions = useMemo(
        () => [`${baseYear - 1}/${baseYear}`, `${baseYear}/${baseYear + 1}`, `${baseYear + 1}/${baseYear + 2}`],
        [baseYear]
    );

    const [educationLevel, setEducationLevel] = useState("Secondary");
    const [academicYear, setAcademicYear] = useState(academicYearOptions[1] || `${baseYear}/${baseYear + 1}`);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        if (!teacherId || !schoolId) return;
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({
                schoolId,
                educationLevel,
                academicYear,
            });
            const { data: payload } = await axios.get(
                `${getApiBaseUrl()}/SchoolCalendar/teacher/${teacherId}/timetable?${params.toString()}`
            );
            setData(payload);
        } catch (err) {
            setError(err.response?.data?.message || "Could not load your timetable.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [teacherId, schoolId, educationLevel, academicYear]);

    useEffect(() => {
        load();
    }, [load]);

    const template = data?.template;
    const grid = data?.grid || {};
    const substitutionCount = data?.substitutionCount || 0;
    const teachingCount = data?.slots?.length || 0;

    return (
        <Box sx={{ py: { xs: 2, md: 3 } }}>
            <Container maxWidth="xl">
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <ScheduleOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography variant="h5" fontWeight={800}>
                                My timetable
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Weekly teaching schedule across all classes, including cover duties.
                            </Typography>
                        </Box>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Education level</InputLabel>
                                    <Select label="Education level" value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)}>
                                        <MenuItem value="Primary">Primary</MenuItem>
                                        <MenuItem value="Secondary">Secondary</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Academic year</InputLabel>
                                    <Select label="Academic year" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                                        {academicYearOptions.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>
                </Stack>

                {error ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <KpiMini title="Teaching slots" value={teachingCount || "—"} subtitle="Regular weekly lessons" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <KpiMini title="Cover duties" value={substitutionCount || "0"} subtitle="Scheduled substitutions" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <KpiMini
                            title="School day"
                            value={template ? `${template.startTime} – ${template.endTime}` : "—"}
                            subtitle={educationLevel === "Primary" ? "30-minute periods" : "40-minute periods"}
                        />
                    </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                    <Box sx={{ px: 2, py: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}>
                        <Typography variant="h6" fontWeight={800}>
                            Weekly schedule
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                        {loading && !data ? (
                            <Skeleton variant="rounded" height={420} />
                        ) : !template?.rows ? (
                            <Alert severity="info">No timetable data for this level and year.</Alert>
                        ) : (
                            <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ minWidth: 720 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Period</TableCell>
                                            {WEEK_DAYS.map((day) => (
                                                <TableCell key={day} align="center">
                                                    {day}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {template.rows.map((row) => (
                                            <TableRow key={row.key}>
                                                <TableCell>
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        {row.label}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.startTime} – {row.endTime}
                                                    </Typography>
                                                </TableCell>
                                                {WEEK_DAYS.map((day) => {
                                                    if (row.type === "break") {
                                                        return (
                                                            <TableCell key={day} align="center">
                                                                <Chip size="small" label={row.label} color="warning" />
                                                            </TableCell>
                                                        );
                                                    }
                                                    const slot = grid[`${row.key}|${day}`];
                                                    return (
                                                        <TableCell key={day} align="center">
                                                            {slot ? (
                                                                <Stack spacing={0.5} alignItems="center">
                                                                    {slot.role === "substitute" ? (
                                                                        <Chip
                                                                            size="small"
                                                                            icon={<SwapHorizOutlinedIcon />}
                                                                            label="Cover"
                                                                            color="info"
                                                                            sx={{ height: 20, fontSize: 10 }}
                                                                        />
                                                                    ) : null}
                                                                    <Typography variant="body2" fontWeight={700}>
                                                                        {slot.subjectName || "—"}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {slot.className}
                                                                    </Typography>
                                                                    {slot.room ? (
                                                                        <Typography variant="caption" color="primary.main">
                                                                            {slot.room}
                                                                        </Typography>
                                                                    ) : null}
                                                                    {slot.role === "substitute" && slot.originalTeacherName ? (
                                                                        <Typography variant="caption" color="text.disabled">
                                                                            For {slot.originalTeacherName}
                                                                        </Typography>
                                                                    ) : null}
                                                                </Stack>
                                                            ) : (
                                                                <Typography variant="caption" color="text.disabled">
                                                                    —
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default TeacherTimetablePage;
